import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, isValid as isValidDate } from 'date-fns';
import { useField } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Improved Weekday Selector with mobile responsiveness
const WeekdaySelector = ({ name }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [field, meta, helpers] = useField(name);

  const weekdays = [
    { value: 'MO', label: t('calendar.eventDialog.weekdays.monday'), short: 'M' },
    { value: 'TU', label: t('calendar.eventDialog.weekdays.tuesday'), short: 'T' },
    { value: 'WE', label: t('calendar.eventDialog.weekdays.wednesday'), short: 'W' },
    { value: 'TH', label: t('calendar.eventDialog.weekdays.thursday'), short: 'T' },
    { value: 'FR', label: t('calendar.eventDialog.weekdays.friday'), short: 'F' },
    { value: 'SA', label: t('calendar.eventDialog.weekdays.saturday'), short: 'S' },
    { value: 'SU', label: t('calendar.eventDialog.weekdays.sunday'), short: 'S' },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.875rem' }}>
        {t('calendar.eventDialog.daysOfWeek')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.25,
          ...(isMobile && {
            gridTemplateColumns: 'repeat(7, minmax(28px, 1fr))',
          }),
        }}
      >
        {weekdays.map(day => (
          <ToggleButton
            key={day.value}
            value={day.value}
            selected={(field.value || []).includes(day.value)}
            onClick={() => {
              const current = field.value || [];
              const newValue = current.includes(day.value)
                ? current.filter(d => d !== day.value)
                : [...current, day.value];
              helpers.setValue(newValue);
            }}
            size="small"
            sx={{
              minWidth: isMobile ? 28 : 36,
              height: isMobile ? 28 : 36,
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'divider',
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            {isMobile ? day.short : day.label.slice(0, 3)}
          </ToggleButton>
        ))}
      </Box>
      {meta.touched && meta.error && (
        <FormHelperText error sx={{ mt: 1 }}>
          {meta.error}
        </FormHelperText>
      )}
    </Box>
  );
};

// Enhanced Monthly Options Component
const MonthlyOptions = ({ repeatType, values, setFieldValue }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (repeatType !== 'monthly') return null;

  const monthlyType = values.recurrence?.monthlyType || 'date';

  const ordinals = [
    { value: 1, label: t('calendar.eventDialog.ordinals.first', 'First') },
    { value: 2, label: t('calendar.eventDialog.ordinals.second', 'Second') },
    { value: 3, label: t('calendar.eventDialog.ordinals.third', 'Third') },
    { value: 4, label: t('calendar.eventDialog.ordinals.fourth', 'Fourth') },
    { value: -1, label: t('calendar.eventDialog.ordinals.last', 'Last') },
  ];

  const weekdays = [
    { value: 0, label: t('calendar.eventDialog.weekdays.sunday', 'Sunday') },
    { value: 1, label: t('calendar.eventDialog.weekdays.monday', 'Monday') },
    { value: 2, label: t('calendar.eventDialog.weekdays.tuesday', 'Tuesday') },
    { value: 3, label: t('calendar.eventDialog.weekdays.wednesday', 'Wednesday') },
    { value: 4, label: t('calendar.eventDialog.weekdays.thursday', 'Thursday') },
    { value: 5, label: t('calendar.eventDialog.weekdays.friday', 'Friday') },
    { value: 6, label: t('calendar.eventDialog.weekdays.saturday', 'Saturday') },
  ];

  return (
    <Card variant="outlined" sx={{ mt: 1.5 }}>
      <CardContent sx={{ p: 2, pb: '16px !important' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}>
          {t('calendar.eventDialog.monthlyOptions', 'Monthly Options')}
        </Typography>

        <RadioGroup value={monthlyType} onChange={e => setFieldValue('recurrence.monthlyType', e.target.value)}>
          <FormControlLabel
            value="date"
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span>{t('calendar.eventDialog.onDay', 'On day')}</span>
                <TextField
                  size="small"
                  hiddenLabel
                  type="number"
                  value={values.recurrence?.monthDay || 1}
                  onChange={e => setFieldValue('recurrence.monthDay', parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 31, style: { textAlign: 'center' } }}
                  sx={{ width: 60 }}
                  disabled={monthlyType !== 'date'}
                />
                <span>{t('calendar.eventDialog.ofMonth', 'of the month')}</span>
              </Box>
            }
            sx={{ mb: 0.5 }}
          />

          <FormControlLabel
            value="weekday"
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span>{t('calendar.eventDialog.onThe', 'On the')}</span>
                <FormControl size="small" hiddenLabel sx={{ minWidth: 80 }}>
                  <Select
                    value={values.recurrence?.weekdayOrdinal || 1}
                    onChange={e => setFieldValue('recurrence.weekdayOrdinal', e.target.value)}
                    disabled={monthlyType !== 'weekday'}
                  >
                    {ordinals.map(ord => (
                      <MenuItem key={ord.value} value={ord.value}>
                        {ord.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" hiddenLabel sx={{ minWidth: 100 }}>
                  <Select
                    value={values.recurrence?.weekday ?? 1}
                    onChange={e => setFieldValue('recurrence.weekday', e.target.value)}
                    disabled={monthlyType !== 'weekday'}
                  >
                    {weekdays.map(day => (
                      <MenuItem key={day.value} value={day.value}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            }
          />
        </RadioGroup>
      </CardContent>
    </Card>
  );
};

// Enhanced Exception Dates Component
const ExceptionDatesField = ({ name }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [field, meta, helpers] = useField(name);
  const [newDate, setNewDate] = useState(null);
  const [showExceptions, setShowExceptions] = useState(false);

  const addException = () => {
    if (newDate && isValidDate(newDate)) {
      const current = field.value || [];
      const dateString = format(newDate, 'yyyy-MM-dd');
      if (!current.includes(dateString)) {
        helpers.setValue([...current, dateString].sort());
      }
      setNewDate(null);
    }
  };

  const removeException = dateString => {
    const current = field.value || [];
    helpers.setValue(current.filter(date => date !== dateString));
  };

  const exceptionCount = (field.value || []).length;

  return (
    <Box>
      <Button
        variant="text"
        onClick={() => setShowExceptions(!showExceptions)}
        endIcon={showExceptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ mb: 0.5, p: 0, justifyContent: 'flex-start', fontWeight: 500, fontSize: '0.875rem' }}
      >
        {t('calendar.eventDialog.exceptionDates', 'Exception Dates')}
        {exceptionCount > 0 && (
          <Chip label={exceptionCount} size="small" sx={{ ml: 1, height: 20, fontSize: '0.75rem' }} />
        )}
      </Button>

      <Collapse in={showExceptions}>
        <Card variant="outlined">
          <CardContent sx={{ p: 2, pb: '16px !important' }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="flex-start">
              <DatePicker
                label={t('calendar.eventDialog.selectDate', 'Select date')}
                value={newDate}
                onChange={setNewDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: isMobile,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={addException}
                disabled={!newDate}
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? 'auto' : 100 }}
              >
                {t('calendar.eventDialog.add', 'Add')}
              </Button>
            </Stack>

            {exceptionCount > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('calendar.eventDialog.excludedDates', 'Excluded dates')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(field.value || []).map(dateString => (
                    <Chip
                      key={dateString}
                      label={format(new Date(dateString), 'MMM d, yyyy')}
                      onDelete={() => removeException(dateString)}
                      deleteIcon={<DeleteIcon />}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>
    </Box>
  );
};

// Main RRULE Builder Component
const RRULEBuilder = ({ values, setFieldValue }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const repeatType = values.recurrence?.repeat_type || 'never';

  const repeatOptions = [
    { value: 'never', label: t('calendar.eventDialog.repeatOptions.never', 'Never') },
    { value: 'daily', label: t('calendar.eventDialog.repeatOptions.daily', 'Daily') },
    { value: 'weekly', label: t('calendar.eventDialog.repeatOptions.weekly', 'Weekly') },
    { value: 'monthly', label: t('calendar.eventDialog.repeatOptions.monthly', 'Monthly') },
  ];

  return (
    <Stack spacing={2}>
      {/* Repeat Type Selector */}
      <FormControl fullWidth>
        <InputLabel>{t('calendar.eventDialog.repeatType', 'Repeat')}</InputLabel>
        <Select
          value={repeatType}
          onChange={e => setFieldValue('recurrence.repeat_type', e.target.value)}
          label={t('calendar.eventDialog.repeatType', 'Repeat')}
        >
          {repeatOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Additional Options */}
      {repeatType !== 'never' && (
        <>
          {/* Weekly Options */}
          {repeatType === 'weekly' && <WeekdaySelector name="recurrence.weekdays" />}

          {/* Monthly Options */}
          <MonthlyOptions repeatType={repeatType} values={values} setFieldValue={setFieldValue} />

          {/* Interval - Moved to bottom per requirements */}
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="flex-start">
            <TextField
              type="number"
              label={t('sites.rrule.every', 'Every')}
              value={values.recurrence?.interval || 1}
              onChange={e => setFieldValue('recurrence.interval', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 999 }}
              size="small"
              sx={{ minWidth: 100 }}
            />
            <Typography
              variant="body2"
              sx={{
                alignSelf: isMobile ? 'flex-start' : 'center',
                mt: isMobile ? 0 : 0.75,
                color: 'text.secondary',
              }}
            >
              {(() => {
                switch (repeatType) {
                  case 'daily':
                    return t('sites.rrule.dayUnit', 'day(s)');
                  case 'weekly':
                    return t('sites.rrule.weekUnit', 'week(s)');
                  case 'monthly':
                    return t('sites.rrule.monthUnit', 'month(s)');
                  default:
                    return repeatType;
                }
              })()}
            </Typography>
          </Stack>

          {/* Exception Dates - Hidden per requirements */}
          {/* <ExceptionDatesField name="recurrence.exception_dates" /> */}
        </>
      )}
    </Stack>
  );
};

export default RRULEBuilder;
